import {Mapper} from '@s-ui/domain'

/**
 * Maps raw contract data from favorites API to DealerEntity with favoriteData metadata
 */
export default class UserFavoriteContractRawDataMapper extends Mapper {
  /**
   * @param {Object} deps
   * @param {Function} deps.entity - DealerEntity factory
   * @param {Object} deps.config - Application config
   * @param {Mapper} deps.locationMapper - Mapper for contract location
   * @param {Mapper} deps.getUrlService - Url service
   */
  constructor({entity, config, locationMapper, getUrlService}) {
    super()
    this._entity = entity
    this._config = config
    this._locationMapper = locationMapper
    this._getUrlService = getUrlService
  }

  _getStockPageUrl = ({dealerSlug, pack}) => {
    if (!dealerSlug) return

    const {DEALER_PACKS} = this._config.get('dealer')
    const hasSubscriptionDealerPack = pack === DEALER_PACKS.SUBSCRIPTION

    return hasSubscriptionDealerPack
      ? this._getUrlService.execute({
          pageName: 'PROFESSIONAL_STOCK_RENTING',
          params: {dealerSlug}
        })
      : this._getUrlService.execute({
          pageName: 'PROFESSIONAL_STOCK',
          params: {dealerSlug}
        })
  }

  /**
   * Maps raw contract data to DealerEntity with favorite metadata
   * @param {Object} rawContract - Raw contract data from API
   * @param {String} rawContract.id - Contract ID
   * @param {String} rawContract.name - Contract name
   * @param {Boolean} rawContract.isActive - Contract name
   * @param {String} rawContract.logoUrl - Contract logo URL
   * @param {Object} rawContract.location - Contract location
   * @param {Object} rawContract.rating - Contract rating
   * @param {Object} rawContract.contact - Contract contact information
   * @param {Object} rawContract.stockPageUrl - Contract stock page url
   * @param {Number} rawContract.activeAdsCount - Number of active ads
   * @param {String} rawContract.createdAt - When contract was favorited
   * @returns {DealerEntity} Mapped contract entity with favoriteData
   */
  map = async rawContract => {
    // Map location if present
    const location = rawContract.location ? this._locationMapper.map(rawContract.location) : undefined
    const {phone: phoneNumber, hasCallTrackingService, hidePhone} = rawContract.contact

    const stockPageUrlVO = await this._getStockPageUrl({
      dealerSlug: rawContract.stockPageUrl,
      pack: rawContract.pack?.type
    })
    const stockPageUrl = stockPageUrlVO && stockPageUrlVO.getUrl()

    // Map API response fields to DealerEntity structure
    const dealerData = {
      id: rawContract.id,
      name: rawContract.name,
      isActive: rawContract.isActive,
      sellerMessagingId: `pro-${rawContract.id}`,
      logoImageUrl: rawContract.logoUrl,
      location,
      ratings: rawContract.rating,
      totalPublishedVehicles: rawContract.activeAdsCount,
      phoneNumber,
      hasCallTrackingService,
      hidePhone,
      pack: rawContract.pack,
      stockPageUrl,
      // Add favorite-specific metadata
      favoriteData: {
        createdAt: rawContract.createdAt,
        dealerDeleted: rawContract.dealerDeleted || false
      },
      isFavorite: true,
      config: this._config
    }

    return this._entity(dealerData)
  }
}
